import React from "react";
import CTASection from "../CTASection";
import Heading, { HeadingLevel } from "../Heading";
import Inner from "../Inner";
import { Link } from "gatsby";
import { ProductBrandInterface } from "../../interfaces/product";
import slugify from "slugify";
import { BodyPortableText } from "../../interfaces/sanity";
import PortableText from "../PortableText";
import { incrementHeadingLevel } from "../../helpers/incrementHeadingLevel";
import Filters from "./components/ProductFiltersList";
import { useProductFilters } from "../../contexts/ProductFilterContext";
import Price from "../Price";
import BrandLink from "../BrandLink";
import { styles } from "./styles";

interface ProductPreviewListProps {
  browseMoreHref?: string;
  title?: string;
  layout?: "vertical" | "horizontal" | "blog" | "product";
  collapsePadding?: boolean;
  headingLevel?: HeadingLevel;
  showBrand?: boolean;
  content?: string | BodyPortableText[];
  includeProductHeading?: boolean;
  enableFilters?: boolean;
}

const renderProductDetails = (
  brand: ProductBrandInterface,
  price: number | null
) => {
  return (
    <>
      <BrandLink title={brand.title} cssStyles={[styles.brand]} />
      <Price price={price} cssStyles={[styles.price]} />
    </>
  );
};

const renderContent = (content?: string | BodyPortableText[]) => {
  if (!content) return null;
  if (typeof content === "string") {
    return <p>{content}</p>;
  }
  return <PortableText blocks={content} />;
};

const ProductPreviewList: React.FC<ProductPreviewListProps> = ({
  title,
  layout = "product",
  headingLevel = "h1",
  collapsePadding,
  showBrand = true,
  content,
  includeProductHeading,
  enableFilters
}) => {
  const {
    filteredProducts,
    availableFilters,
    containerRef
  } = useProductFilters();
  return (
    <Inner collapseAll={collapsePadding}>
      {title && (
        <Heading
          headingLevel={headingLevel}
          styles={
            layout === "horizontal"
              ? styles.productHeading_horizontal
              : undefined
          }
        >
          {title}
        </Heading>
      )}
      {renderContent(content)}
      {filteredProducts && filteredProducts.length ? (
        <div css={styles.productListOuter} ref={containerRef}>
          {includeProductHeading && !enableFilters && (
            <Heading headingLevel={incrementHeadingLevel(headingLevel, 1)}>
              Products
            </Heading>
          )}
          <div css={styles.productListContainer}>
            {enableFilters && availableFilters && (
              <Filters
                headingLevel={incrementHeadingLevel(
                  headingLevel,
                  includeProductHeading ? 2 : 1
                )}
              />
            )}
            <ul
              css={[
                styles.productList,
                styles[`productList_${layout}`],
                enableFilters &&
                  availableFilters &&
                  styles.productListWithFilters
              ]}
            >
              {filteredProducts.map(
                ({ brand, slug, id, title, mainImage, price }, i) => {
                  const productSlug = `/shop/${slugify(brand.title, {
                    lower: true
                  })}/${slug.current}`;
                  return (
                    <li
                      key={id}
                      css={[
                        styles.productPreviewItem,
                        layout === "horizontal" && styles.product_horizontal,
                        i === filteredProducts.length - 1 &&
                          layout === "horizontal" &&
                          styles.productLast
                      ]}
                    >
                      <CTASection
                        layout={layout}
                        theme="light"
                        title={
                          <Link to={productSlug} css={styles.productTitleLink}>
                            {title}
                          </Link>
                        }
                        cta={{
                          href: productSlug,
                          content: "Read more here"
                        }}
                        content={
                          showBrand
                            ? renderProductDetails(brand, price)
                            : undefined
                        }
                        image={mainImage}
                        collapseAllPadding={true}
                        headingLevel={
                          availableFilters
                            ? incrementHeadingLevel(headingLevel, 3)
                            : "h2"
                        }
                      />
                    </li>
                  );
                }
              )}
            </ul>
          </div>
        </div>
      ) : null}
    </Inner>
  );
};

export default ProductPreviewList;
